/**
 * @typedef {Object} El
 *
 * @property {boolean} exists
 * @property {HTMLElement[]} elements
 * @property {HTMLElement} element
 * @property {string} selector
 */

/**
 * @typedef {Object} BAOCustomElementOptions
 *
 * @property {typeof HTMLElement} SuperClass
 */

/**
 * @param {BAOCustomElementOptions} - options
 */
window.BAO.CustomElement = function BAOCustomElement({ SuperClass = HTMLElement } = {}) {
  class CustomElement extends SuperClass {
    /**
     * @returns {string[]}
     */
    static get requiredElements() {
      return [];
    }

    /**
     * @returns {string[]}
     */
    static get requiredExternalElements() {
      return [];
    }

    static get styles() {
      return '';
    }

    constructor() {
      super();

      /**
       * @private
       * @type {boolean}
       */
      this._hasBeenInitialised = false;

      /**
       * sections - a list of sections that are related to this element. They can be updated by
       * calling the renderSections method with updated HTML
       * @type {Section[]}
       * @public
       */
      this.sections = [];

      /** @type {Object.<string, El>} */
      this.els = this.getElements(this._getElementIdentifiers());
      /** @type {Object.<string, El>} */
      this.externalEls = this.getElements(
        this._getExternalElementIdentifiers(),
        this._externalElIdentifier,
        { context: document }
      );
      this.listeners = new window.BAO.Listeners();

      this.checkValidity();

      if (this.constructor.styles !== '') {
        const shadow = this.attachShadow({ mode: 'open' });
        const style = document.createElement('style');
        const slot = document.createElement('slot');

        shadow.appendChild(style);
        shadow.appendChild(slot);
      }
    }

    connectedCallback() {
      if (this._hasBeenInitialised) return;

      this._hasBeenInitialised = true;

      this.setupListeners();
      this.applyStyles();
    }

    disconnectedCallback() {
      this.listeners.removeAll();
    }

    checkValidity() {
      if (
        this.constructor.requiredElements.length === 0 &&
        this.constructor.requiredExternalElements.length === 0
      ) {
        return;
      }

      const requiredElementsNotPresent = this.constructor.requiredElements.filter(
        (name) => !(name in this.els)
      );
      const requiredExternalElementsNotPresent = this.constructor.requiredExternalElements.filter(
        (name) => !(name in this.externalEls)
      );

      if (
        requiredElementsNotPresent.length === 0 &&
        requiredExternalElementsNotPresent.length === 0
      ) {
        return;
      }

      const requiredElementsMessages = requiredElementsNotPresent
        .map((name) => `- ${this._elIdentifier}="${name}"`)
        .join('\n');
      const requiredExternalElementsMessages = requiredExternalElementsNotPresent
        .map((name) => `- ${this._externalElIdentifier}="${name}"`)
        .join('\n');

      let message = 'Has errors, please read below';

      if (requiredElementsNotPresent.length > 0) {
        message += `

Missing required elements:
  ${requiredElementsMessages}`;
      }

      if (requiredExternalElementsNotPresent.length > 0) {
        message += `

Missing external required elements:
  ${requiredExternalElementsMessages}`;
      }

      this.throwError(message);
    }

    /**
     * @param {string} message
     * @throws {Error}
     */
    throwError(message) {
      // eslint-disable-next-line no-throw-literal
      throw `<${this.identifier}>: ${message}`;
    }

    setupListeners() {}

    applyStyles() {
      if (this.constructor.styles === '') return;

      const shadow = this.shadowRoot;
      const style = shadow.querySelector('style');
      if (!style) return;

      style.textContent = this.constructor.styles;
    }

    /**
     * @param {string[]} identifiers
     * @param {string} elementIdentifier
     * @param {{ context: BAOCustomElement }}
     *
     * @returns {Object.<string, El>}
     */
    getElements(identifiers, elementIdentifier = this._elIdentifier, { context = this } = {}) {
      return identifiers.reduce((els, identifier) => {
        const subIdentifiers = identifier.split(' ');

        subIdentifiers.forEach((_identifier) => {
          const selector = (() => {
            const attributeSelector = subIdentifiers.length > 1 ? '*=' : '=';
            const identifierToLookFor = subIdentifiers.length > 1 ? _identifier : identifier;

            return this.key && elementIdentifier === this._externalElIdentifier
              ? `[${elementIdentifier}${attributeSelector}"${identifierToLookFor}"][key="${this.key}"]`
              : `[${elementIdentifier}${attributeSelector}"${identifierToLookFor}"]`;
          })();
          const elements = Array.from(context.querySelectorAll(selector));

          els[_identifier] = {
            exists: elements.length > 0,
            elements,
            element: elements[0],
            selector,
          };
        });

        return els;
      }, {});
    }

    /**
     * @param {string} prop
     *
     * @returns {void}
     */
    _upgradeProperty(prop) {
      // eslint-disable-next-line no-prototype-builtins
      if (this.hasOwnProperty(prop)) {
        const value = this[prop];
        delete this[prop];
        this[prop] = value;
      }
    }

    /**
     * @returns {string[]}
     */
    _getElementIdentifiers() {
      return Array.from(this.querySelectorAll(`[${this._elIdentifier}]`)).map((el) =>
        el.getAttribute(this._elIdentifier)
      );
    }

    /**
     * @returns {string[]}
     */
    _getExternalElementIdentifiers() {
      const selector = this.key
        ? `[${this._externalElIdentifier}][key="${this.key}"]`
        : `[${this._externalElIdentifier}]`;

      return Array.from(document.querySelectorAll(selector)).map((el) =>
        el.getAttribute(this._externalElIdentifier)
      );
    }

    /**
     * Renders multiple sections based on sections returned from the cart AJAX API
     *
     * @param {Object} stateSections - An object where the keys are section ids and the values are
     *   the HTML returned from the section rendering API
     *
     * @returns {void}
     */
    renderSections(stateSections) {
      if (this.sections.length === 0) return;

      const sectionsToRender = this.sections
        .filter((s) => s.section in stateSections)
        .map((s) => Object.assign({}, s, { html: stateSections[s.section] }));

      window.BAO.sectionRenderer.renderMultiple(sectionsToRender);
    }

    /**
     * @param {string} groupName
     * @param {*[]} otherItems
     *
     * @returns {undefined}
     */
    log(groupName, ...otherItems) {
      if (!this.debug) return;

      /* eslint-disable no-console */
      console.group(groupName);

      otherItems.forEach((item) => console.log(item));

      console.groupEnd();
      /* eslint-enable no-console */
    }

    /**
     * @returns {string}
     */
    get identifier() {
      const baseIdentifier = this.hasAttribute('is')
        ? this.getAttribute('is')
        : this.localName.toLowerCase();

      return this.key && this.key.trim().length
        ? `${baseIdentifier}-${this.key.toLowerCase()}`
        : baseIdentifier;
    }

    /**
     * @returns {boolean}
     */
    get debug() {
      return window.theme.isDevTheme;
    }

    /**
     * @returns {string|null}
     */
    get key() {
      return this.getAttribute('key');
    }

    /**
     * @returns {boolean}
     */
    get hasBeenInitialised() {
      return this._hasBeenInitialised;
    }

    /**
     * @private
     * @returns {string}
     */
    get _elIdentifier() {
      return `data-${this.identifier}-el`;
    }

    /**
     * @private
     * @returns {string}
     */
    get _externalElIdentifier() {
      return `data-${this.identifier}-external-el`;
    }
  }

  return CustomElement;
};
